import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import { Icon } from '@iconify/react';
import chevronDoubleLeft from '@iconify/icons-mdi/chevron-double-left';
import chevronLeft from '@iconify/icons-mdi/chevron-left';
import chevronDoubleRight from '@iconify/icons-mdi/chevron-double-right';
import chevronRight from '@iconify/icons-mdi/chevron-right';
import Theme from '../common/NekoTheme';

const PagingContainer = Styled.div`
  align-items: center;
  display: flex;

  .neko-paging-text {
    font-family: ${Theme.fontFamily};
    font-style: normal;
    font-weight: normal;
    font-size: 15px;
    line-height: 14px;
  }

  .neko-paging-controller {
    box-sizing: border-box;
    height: 30px;
    align-items: center;
    background-color: rgba(${Theme.blueRGB}, 0.8);
    border-radius: 15px;
    display: flex;
    margin-left: 15px;
    padding: 3px 5px;

    .nako-paging-controller-icon {
      background-color: ${Theme.white};
      border-radius: 100%;
      cursor: pointer;
      margin-right: 2px;
      height: 22px;
      width: 22px;
      box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
      transition: transform 0.1s ease-in;
      box-sizing: border-box;

      :last-child {
        margin-right: 0;
      }

      &.disabled {
        color: ${Theme.darkGray};
        cursor: default;
        pointer-events: none;
      }

      &:hover {
        transform: scale(1.2) !important;
        z-index: 10;
        position: relative;
      }
    }

    .nako-paging-controller-text {
      color: ${Theme.white};
      font-family: ${Theme.fontFamily};
      font-style: normal;
      font-weight: normal;
      font-size: 13px;
      margin: 0 40px;
      user-select: none;
    }

    span.neko-paging-current-page {
      cursor: pointer;
      text-decoration: underline;
    }
    input.neko-paging-current-page {
      width: 1.5rem;
    }
  }

  &.neko-paging-full {
    flex-grow: 1;

    .neko-paging-controller {
      flex-grow: 1;
    }
    .nako-paging-controller-text {
      margin: 0 auto;
    }
  }
`;

/**
 * @param {number} currentPage Current page
 * @param {number} limit Items per page
 */
const StyledNekoPaging = props => {
  const { currentPage, limit, onClick, total, onCurrentPageChanged, full } = props;
  const isEditable = !!onCurrentPageChanged;
  const maxPage = Math.ceil(total === 0 ? 1 : (limit > 0 ? (total / limit) : 1));
  const prevIconClassName = `nako-paging-controller-icon ${currentPage === 1 ? 'disabled' : ''}`;
  const nextIconClassName = `nako-paging-controller-icon ${currentPage === maxPage ? 'disabled' : ''}`;
  const [ showCurrentPageInput, setShowCurrentPageInput ] = useState(false);
  const fullClassName = full ? 'neko-paging-full' : '';

  const onClickHandle = (page) => {
    setShowCurrentPageInput(false);
    onClick(page);
  };

  const getValidPage = (page) => {
    const numberPage = Number(page);
    return numberPage > maxPage ? maxPage : (numberPage < 1 ? 1 : numberPage);
  }

  const onBlur = (e) => {
    const page = e.target.value;

    if (!isNaN(page)) {
      onCurrentPageChanged(getValidPage(page));
    }
    setShowCurrentPageInput(false);
  }

  const onKeyPress = (e) => {
    if (event.key === 'Enter') {
      e.preventDefault();
      const page = e.target.value;
      if (!isNaN(page)) {
        onCurrentPageChanged(getValidPage(page));
      }
      setShowCurrentPageInput(false);
    }
  };

  const currentPageJsx = useMemo(() => {
    if (!showCurrentPageInput) {
      const onClick = () => {
        if (!isEditable) {
          return;
        }
        setShowCurrentPageInput(true);
      }
      return <span className={isEditable ? 'neko-paging-current-page' : ''} onClick={onClick}>{currentPage}</span>;
    }
    return <input autoFocus type="text" className={isEditable ? 'neko-paging-current-page' : ''} 
      defaultValue={currentPage} onBlur={onBlur} onKeyPress={onKeyPress} />;
  }, [currentPage, showCurrentPageInput, onCurrentPageChanged]);

  const onControllerClick = (e) => {
    if (!showCurrentPageInput || e.target !== e.currentTarget) return;

    setShowCurrentPageInput(false);
  }

  return (
    <PagingContainer className={fullClassName}>

      <span className="neko-paging-text">{total} result{total > 0 ? 's' : ''}</span>

      <div className="neko-paging-controller" onClick={onControllerClick}>
        <Icon icon={chevronDoubleLeft}
          className={prevIconClassName}
          onClick={() => onClickHandle(1)}
        />
        <Icon icon={chevronLeft}
          className={prevIconClassName}
          onClick={() => onClickHandle(currentPage - 1)}
        />
        <p className="nako-paging-controller-text" onClick={onControllerClick}>
          Page {currentPageJsx} of {maxPage}
        </p>
        <Icon icon={chevronRight}
          className={nextIconClassName}
          onClick={() => onClickHandle(currentPage + 1)}
        />
        <Icon icon={chevronDoubleRight}
          className={nextIconClassName}
          onClick={() => onClickHandle(maxPage)}
        />
      </div>
    </PagingContainer>
  );
};

const NekoPaging = (props) => {
  return (<StyledNekoPaging {...props} />);
};

NekoPaging.propTypes = {
  currentPage: PropTypes.number,
  limit: PropTypes.number,
  total: PropTypes.number,
  onClick: PropTypes.func
};

NekoPaging.defaultProps = {
  currentPage: undefined,
  limit: undefined,
  total: undefined,
  onClick: undefined
};

export { NekoPaging };
